import axios from 'axios';
import { API_CONFIG } from '../constants/index.js';

/**
 * Leaderboard Service
 * Handles API calls for leaderboard data and rankings
 */
class LeaderboardService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.leaderboardEndpoint = API_CONFIG.ENDPOINTS.LEADERBOARDS;
  }

  /**
   * Get top projects by funding amount
   * @param {string} timeframe - 'all', 'month', 'week'
   * @param {number} limit - Number of projects to fetch
   * @returns {Promise<Object>} Leaderboard data
   */
  async getTopProjectsByFunding(timeframe = 'all', limit = 10) {
    try {
      //console.log('Fetching funding leaderboard:', { timeframe, limit });
      
      const response = await axios.get(`${this.baseURL}${this.leaderboardEndpoint}/funding`, {
        params: { timeframe, limit }
      });
      
      if (response.data.success) {
        return this.formatLeaderboard(response.data.data);
      } else {
        throw new Error(response.data.error?.message || 'Failed to fetch funding leaderboard');
      }
    } catch (error) {
      console.error('Funding leaderboard fetch error:', error);
      return this.getFallbackLeaderboard('funding', timeframe);
    }
  }

  /**
   * Get top projects by number of supporters
   * @param {string} timeframe - 'all', 'month', 'week'
   * @param {number} limit - Number of projects to fetch
   * @returns {Promise<Object>} Leaderboard data
   */
  async getTopProjectsBySupporters(timeframe = 'all', limit = 10) {
    try {
      //console.log('Fetching supporters leaderboard:', { timeframe, limit });
      
      const response = await axios.get(`${this.baseURL}${this.leaderboardEndpoint}/supporters`, {
        params: { timeframe, limit }
      });
      
      if (response.data.success) {
        return this.formatLeaderboard(response.data.data);
      } else {
        throw new Error(response.data.error?.message || 'Failed to fetch supporters leaderboard');
      }
    } catch (error) {
      console.error('Supporters leaderboard fetch error:', error);
      return this.getFallbackLeaderboard('supporters', timeframe);
    }
  }

  /**
   * Get trending projects (recent activity)
   * @param {number} limit - Number of projects to fetch
   * @returns {Promise<Object>} Trending projects data
   */
  async getTrendingProjects(limit = 5) {
    try {
      //console.log('Fetching trending projects:', { limit });
      
      const response = await axios.get(`${this.baseURL}${this.leaderboardEndpoint}/trending`, {
        params: { limit }
      });
      
      if (response.data.success) {
        return this.formatLeaderboard(response.data.data);
      } else {
        throw new Error(response.data.error?.message || 'Failed to fetch trending projects');
      }
    } catch (error) {
      console.error('Trending projects fetch error:', error);
      return this.getFallbackLeaderboard('trending', 'week');
    }
  }

  /**
   * Get success stories (completed projects that met goals)
   * @param {number} limit - Number of projects to fetch
   * @returns {Promise<Object>} Success stories data
   */
  async getSuccessStories(limit = 5) {
    try {
      //console.log('Fetching success stories:', { limit });
      
      const response = await axios.get(`${this.baseURL}${this.leaderboardEndpoint}/success`, {
        params: { limit }
      });
      
      if (response.data.success) {
        return this.formatLeaderboard(response.data.data);
      } else {
        throw new Error(response.data.error?.message || 'Failed to fetch success stories');
      }
    } catch (error) {
      console.error('Success stories fetch error:', error);
      return this.getFallbackLeaderboard('success', 'all');
    }
  }

  /**
   * Get all leaderboard data at once
   * @returns {Promise<Object>} All leaderboards data
   */
  async getAllLeaderboards() {
    try {
      //console.log('Fetching all leaderboards');
      
      const response = await axios.get(`${this.baseURL}${this.leaderboardEndpoint}/all`);
      
      if (response.data.success) {
        const data = response.data.data;
        return {
          funding: data.funding || [],
          supporters: data.supporters || [],
          trending: data.trending || [],
          success: data.success || [],
          last_updated: data.last_updated,
          fallback: false
        };
      } else {
        throw new Error(response.data.error?.message || 'Failed to fetch all leaderboards');
      }
    } catch (error) {
      console.error('All leaderboards fetch error:', error);
      return this.getFallbackAllLeaderboards();
    }
  }

  /**
   * Get fallback leaderboard data when API fails
   * @param {string} type - Leaderboard type
   * @param {string} timeframe - Timeframe
   * @returns {Object} Fallback leaderboard data
   */
  getFallbackLeaderboard(type, timeframe) {
    return {
      leaderboard: [],
      timeframe,
      total_count: 0,
      last_updated: new Date().toISOString(),
      fallback: true,
      type
    };
  }

  /**
   * Get fallback data for all leaderboards
   * @returns {Object} Fallback data
   */
  getFallbackAllLeaderboards() {
    return {
      funding: [],
      supporters: [],
      trending: [],
      success: [],
      last_updated: new Date().toISOString(),
      fallback: true
    };
  }

  /**
   * Format leaderboard data for display
   * @param {Object} leaderboardData - Raw leaderboard data from API
   * @returns {Object} Formatted leaderboard data
   */
  formatLeaderboard(leaderboardData) {
    if (!leaderboardData || !leaderboardData.leaderboard) {
      return this.getFallbackLeaderboard('unknown', 'all');
    }

    return {
      ...leaderboardData,
      leaderboard: leaderboardData.leaderboard.map(project => this.formatProject(project)),
      last_updated_display: new Date(leaderboardData.last_updated).toLocaleString(),
      fallback: false
    };
  }

  /**
   * Format individual project data for display
   * @param {Object} project - Raw project data from API
   * @returns {Object} Formatted project data
   */
  formatProject(project) {
    return {
      ...project,
      funding_display: project.display_funding || this.formatCurrency(project.current_funding, project.currency),
      goal_display: project.funding_goal ? this.formatCurrency(project.funding_goal, project.currency) : 'No goal set',
      progress_display: `${Math.round(project.progress_percentage || 0)}%`,
      supporters_display: this.formatNumber(project.supporter_count),
      created_display: new Date(project.created_at).toLocaleDateString(),
      owner_display: project.owner_username || 'Anonymous',
      rank_display: this.getRankDisplay(project.rank),
      status_badge: this.getStatusBadge(project.status),
      category_display: this.getCategoryDisplay(project.category)
    };
  }

  /**
   * Format currency values
   * @param {string|number} amount - Amount to format
   * @returns {string} Formatted currency
   */
  formatCurrency(amount, currency = 'PI') {
    if (!amount && amount !== 0) return currency === 'NGN' ? "₦ 0" : "π 0";
    const num = parseFloat(amount);
    const symbol = currency === 'NGN' ? '₦' : 'π';
    
    if (num >= 1000000) {
      return `${symbol} ${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${symbol} ${(num / 1000).toFixed(1)}K`;
    } else {
      return `${symbol} ${num.toLocaleString()}`;
    }
  }

  /**
   * Format numbers with commas
   * @param {number} num - Number to format
   * @returns {string} Formatted number
   */
  formatNumber(num) {
    if (!num && num !== 0) return "0";
    return num.toLocaleString();
  }

  /**
   * Get timeframe display text
   * @param {string} timeframe - Timeframe key
   * @returns {string} Display text
   */
  getTimeframeDisplay(timeframe) {
    const timeframes = {
      'all': 'All Time',
      'month': 'This Month',
      'week': 'This Week'
    };
    return timeframes[timeframe] || 'All Time';
  }

  /**
   * Get category display text
   * @param {string} category - Category key
   * @returns {string} Display text
   */
  getCategoryDisplay(category) {
    const categories = {
      'medical': 'Medical Relief',
      'emergency': 'Emergency Aid',
      'app_development': 'App Development',
      'personal': 'Personal Causes',
      'education': 'Education',
      'environment': 'Environmental',
      'technology': 'Technology',
      'community': 'Community',
      'other': 'Other'
    };
    return categories[category] || category || 'General';
  }

  /**
   * Get status badge configuration
   * @param {string} status - Project status
   * @returns {Object} Badge configuration
   */
  getStatusBadge(status) {
    const statusConfigs = {
      'active': { text: 'Active', color: 'bg-green-100 text-green-800' },
      'completed': { text: 'Completed', color: 'bg-blue-100 text-blue-800' },
      'draft': { text: 'Draft', color: 'bg-gray-100 text-gray-800' },
      'submitted': { text: 'Under Review', color: 'bg-yellow-100 text-yellow-800' },
      'rejected': { text: 'Rejected', color: 'bg-red-100 text-red-800' },
      'cancelled': { text: 'Cancelled', color: 'bg-orange-100 text-orange-800' }
    };
    return statusConfigs[status] || { text: status || 'Unknown', color: 'bg-gray-100 text-gray-800' };
  }

  /**
   * Get rank display with appropriate styling
   * @param {number} rank - Project rank
   * @returns {Object} Rank display configuration
   */
  getRankDisplay(rank) {
    const configs = {
      1: { text: '🥇', color: 'text-yellow-600', bg: 'bg-yellow-50', textFallback: '#1' },
      2: { text: '🥈', color: 'text-gray-600', bg: 'bg-gray-50', textFallback: '#2' },
      3: { text: '🥉', color: 'text-amber-600', bg: 'bg-amber-50', textFallback: '#3' }
    };
    
    return configs[rank] || { 
      text: `#${rank}`, 
      color: 'text-primary-teal', 
      bg: 'bg-primary-teal/10',
      textFallback: `#${rank}`
    };
  }
}

// Create and export a singleton instance
const leaderboardService = new LeaderboardService();
export default leaderboardService;